/**
 * Authentication Context for NWGSV Command System
 * Manages user authentication state and provides auth methods
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiCall, formatApiErrorDetail } from '../utils/api';

export type UserRole = 'SUPER_ADMIN' | 'FAMILY' | 'INVESTOR' | 'AFFILIATE' | 'CUSTOMER';

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  status: string;
  created_at: string;
  last_login?: string;
}

interface AuthContextType {
  user: User | null | undefined; // undefined = checking, null = not logged in, User = logged in
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const refreshSession = async (): Promise<boolean> => {
    try {
      const res = await apiCall('/api/auth/refresh', { method: 'POST' });
      return res.ok;
    } catch {
      return false;
    }
  };

  const checkAuth = async () => {
    try {
      let response = await apiCall('/api/auth/me');
      if (response.status === 401) {
        // Access cookie expired — try the 7-day refresh cookie before giving up
        const refreshed = await refreshSession();
        if (refreshed) response = await apiCall('/api/auth/me');
      }
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Keep the 15-minute access cookie alive while logged in.
  // Without this, API calls silently 401 after 15 min while the header shows a cached user.
  useEffect(() => {
    if (!user) return;
    let lastRefresh = Date.now();
    const doRefresh = async () => {
      const ok = await refreshSession();
      if (ok) {
        lastRefresh = Date.now();
      } else {
        setUser(null); // refresh token expired — show honest logged-out state
      }
    };
    const interval = setInterval(doRefresh, 10 * 60 * 1000);
    const onFocus = () => {
      if (Date.now() - lastRefresh > 60 * 1000) doRefresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [user]);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      const userData = await response.json();

      if (!response.ok) {
        throw new Error(formatApiErrorDetail(userData.detail));
      }

      setUser(userData);
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  };

  const register = async (email: string, password: string, fullName: string) => {
    try {
      const response = await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, full_name: fullName }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(formatApiErrorDetail(data.detail));
      }

      setUser(data);
    } catch (error: any) {
      throw new Error(error.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      await apiCall('/api/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
